import { QuantitySelectorComponent } from '@theme/component-quantity-selector';

/**
 * A custom element that allows the user to select a quantity in the cart.
 * Extends QuantitySelectorComponent but uses absolute max limits instead of effective max.
 * Semantics: "What should the total quantity BE in the cart" vs "How many to ADD to cart"
 *
 * @extends {QuantitySelectorComponent}
 */
class CartQuantitySelectorComponent extends QuantitySelectorComponent {
  /**
   * Gets the effective maximum value for cart quantity selector
   * Cart page: uses absolute max (how much can be in cart total)
   * @returns {number | null} The effective max, or null if no max
   */
  getEffectiveMax() {
    const { max } = this.getCurrentValues();
    return max; // Cart uses absolute max, not max minus cart quantity
  }

  /**
   * Updates button states based on current value and limits
   * Cart buttons are always managed client-side, never server-disabled
   */
  updateButtonStates() {
    const { minusButton, plusButton } = this.refs;
    const { value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    // Keep minus enabled at the minimum quantity: in the cart, stepping below
    // the minimum removes the line item (see decreaseQuantity), so the button
    // must stay clickable. Only disable when there's nothing left to remove.
    // Honor server-disabled state so non-updatable lines (can_update_quantity
    // == false) stay disabled and can't be removed via the minus button.
    if (!this.serverDisabledMinus) {
      minusButton.disabled = value <= 0;
    }
    if (!this.serverDisabledPlus) {
      plusButton.disabled = effectiveMax !== null && value >= effectiveMax;
    }
  }

  /**
   * Decreases the cart quantity. At (or below) the minimum, stepping down
   * removes the line item instead of clamping: it dispatches a quantity-0
   * update, which cart-items-component handles as a removal.
   * @param {Event} event - The event.
   */
  decreaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) {
      return;
    }
    event.preventDefault();

    const { min, value } = this.getCurrentValues();

    if (value - 1 < min) {
      this.refs.quantityInput.value = '0';
      this.onQuantityChange();
      return;
    }

    this.updateQuantity(-1);
  }
}

if (!customElements.get('cart-quantity-selector-component')) {
  customElements.define('cart-quantity-selector-component', CartQuantitySelectorComponent);
}
